import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL, STATUS_LABELS, MONTHS, formatDateOnly } from "./constants/adminConstants";
import AdminChangeDetail from "./AdminChangeDetail";
import "./css/adminStyles.css";

function AdminMyChanges({ apiEndpoint }) {
  const [changeRequests, setChangeRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");

  useEffect(() => {
    fetchMyChanges();
  }, []);

  useEffect(() => {
    let filtered = [...changeRequests];

    if (statusFilter) {
      filtered = filtered.filter((cr) => cr.status === statusFilter);
    }

    if (monthFilter) {
      filtered = filtered.filter((cr) => {
        const d = cr.createdAt ? new Date(cr.createdAt) : null;
        return d && d.getMonth() === parseInt(monthFilter);
      });
    }

    if (yearFilter) {
      filtered = filtered.filter((cr) => {
        const d = cr.createdAt ? new Date(cr.createdAt) : null;
        return d && d.getFullYear() === parseInt(yearFilter);
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (cr) =>
          (cr.description || "").toLowerCase().includes(q) ||
          (cr.status || "").toLowerCase().includes(q)
      );
    }

    setFilteredRequests(filtered);
  }, [statusFilter, monthFilter, yearFilter, searchQuery, changeRequests]);

  const fetchMyChanges = async () => {
    const token = sessionStorage.getItem("tvc_token");
    const endpoint = apiEndpoint || "/staff/my-changes";
    try {
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setChangeRequests(data.changeRequests || []);
        setFilteredRequests(data.changeRequests || []);
      }
    } catch (err) {
      console.error("Fetch my changes error:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending_manager": return "AdminBadgePending";
      case "manager_approved": return "AdminBadgePending";
      case "admin_approved": return "AdminBadgeAdminApproved";
      case "rejected": return "AdminBadgeRejected";
      default: return "AdminBadgePending";
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear - i);

  if (loading) {
    return <div className="AdminEmptyState">Loading your change requests...</div>;
  }

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
         <h3 style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 20, fontWeight: 700, color: "#1B2A4A" }}>My Change Requests <span className="AdminCountBadge">{filteredRequests.length}</span></h3>
        <div className="AdminPillFilterBar">
          <div className="AdminPillGroup">
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                 onBlur={() => { setSearchQuery(""); setTimeout(() => setSearchOpen(false), 200) }}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
          </div>

          <div className="AdminPillGroup">
            <div className="AdminPillFilterItem">
              <select className="AdminPillSelect" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="pending_manager">Pending Manager</option>
                <option value="manager_approved">Manager Approved</option>
                <option value="admin_approved">Admin Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="AdminPillFilterItem">
              <select className="AdminPillSelect" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
                <option value="">Months</option>
                {MONTHS.map((m, i) => (
                  <option key={i} value={i}>{m}</option>
                ))}
              </select>
              {monthFilter && (
                <button className="AdminPillClear" onClick={() => setMonthFilter("")}>&times;</button>
              )}
            </div>

            <div className="AdminPillFilterItem">
              <select className="AdminPillSelect" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                <option value="">Years</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              {yearFilter && (
                <button className="AdminPillClear" onClick={() => setYearFilter("")}>&times;</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="AdminEmptyState">No change requests found.</div>
      ) : (
        <div className="AdminTableScroll">
        <table className="AdminTable">
          <thead>
            <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((cr) => (
                <React.Fragment key={cr.id}>
                  <tr>
                    <td>{formatDateOnly(cr.createdAt)}</td>
                    <td style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {cr.description}
                    </td>
                    <td>
                      <span className={`AdminBadge ${getStatusBadgeClass(cr.status)}`}>
                        {{ ...STATUS_LABELS, pending_manager: "Pending", manager_approved: "Pending" }[cr.status] || cr.status}
                      </span>
                    </td>
                  <td>
                    <button className="AdminExpandBtn" onClick={() => toggleExpand(cr.id)}>
                      {expandedId === cr.id ? "Hide" : "View Details"}
                    </button>
                  </td>
                </tr>
                {expandedId === cr.id && (
                  <tr>
                    <td colSpan={4} style={{ padding: "16px 24px", background: "#f8fafc" }}>
                      <AdminChangeDetail changeRequest={cr} />
                      {cr.managerNote && (
                        <div style={{ marginTop: 8, padding: 8, background: "#dbeafe", borderRadius: 6, fontSize: 13 }}>
                          <strong>Manager Note:</strong> {cr.managerNote}
                        </div>
                      )}
                      {cr.adminNote && (
                        <div style={{ marginTop: 8, padding: 8, background: "#d1fae5", borderRadius: 6, fontSize: 13 }}>
                          <strong>Admin Note:</strong> {cr.adminNote}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

export default AdminMyChanges;
